import React, { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import Button from "components/Button";
import { Board, TitleArea, ContentArea, FormRow, FooterArea } from "components/LayoutParts";
import UserModal, { UserModalConfig } from "components/UserModal";
import font from "constants/font";
import AimoParkingSelect from "pages/AimoParking/component/AimoParkingSelect";
import { useFindEdgeDevice, useUpdateEdgeDeviceStatus } from "pages/AimoParking/EdgeDevice/api";
import {
  CONFIRM_MODAL_CONFIG,
  FAILED_MODAL_CONFIG,
  INITIAL_MODAL_CONFIG,
  SUCCEED_MODAL_CONFIG,
} from "pages/AimoParking/modalTemplate";
import {
  AIMO_PARKING_EDGE_DEVICE_UPDATABLE_STATUS,
  AimoParkingEdgeDeviceStatus,
  generateAimoParkingEdgeDeviceStatusJa,
} from "pages/AimoParking/utils/aimoParkingEdgeDeviceStatusUtil";

const Label = styled.div`
  min-width: 200px;
  margin: 0 5px;
  padding-right: 18px;
`;

const Value = styled.div`
  font-size: ${font.size16};
`;

const Wrap = styled.div`
  margin-top: 10px;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  flex: 1;
`;

const CancelWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;

const SaveWrap = styled(CancelWrap)`
  align-items: center;
  justify-content: flex-end;
`;

const Annotation = styled.span`
  font-size: ${font.size12};
  margin-right: 16px;
`;

type ChoiceType = { key?: number | string; label: string; value: string; disabled?: boolean };

const choices: ChoiceType[] = AIMO_PARKING_EDGE_DEVICE_UPDATABLE_STATUS.map((status) => ({
  key: status,
  label: generateAimoParkingEdgeDeviceStatusJa(status) ?? "",
  value: String(status),
}));

const EdgeDeviceUpdateStatus = () => {
  const { id: edgeDeviceId } = useParams();
  const navigate = useNavigate();
  const [selectedChoice, setSelectedChoice] = useState<ChoiceType | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState<UserModalConfig>(INITIAL_MODAL_CONFIG);

  const { data: edgeDevice } = useFindEdgeDevice({ edgeDeviceId: edgeDeviceId ?? "" });
  const { updateStatus } = useUpdateEdgeDeviceStatus();

  const canSubmit = selectedChoice != null;

  const onSubmit = useCallback(async () => {
    if (edgeDeviceId == null || selectedChoice == null) return;
    const response = await updateStatus({
      edgeDeviceId,
      status: Number(selectedChoice.value) as AimoParkingEdgeDeviceStatus,
    }).catch((e) => {
      setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
      setIsVisible(true);
      throw e;
    });

    if (response.status === 200) {
      setModalConfig(
        SUCCEED_MODAL_CONFIG({
          message: "送信しました",
          onClose: () => navigate(`/aimo_parking/edge_device/${edgeDeviceId}`),
        })
      );
      setIsVisible(true);
    }
  }, [edgeDeviceId, selectedChoice, updateStatus, navigate]);

  const handleOpenSubmitModal = useCallback(() => {
    setModalConfig(
      CONFIRM_MODAL_CONFIG({
        negative: () => setIsVisible(false),
        positive: onSubmit,
        message: "ステータスを更新しますか？",
      })
    );
    setIsVisible(true);
  }, [onSubmit]);

  return (
    <Board>
      <TitleArea>
        <Breadcrumb
          currentPageName="ステータスを変更"
          breadcrumbItems={[
            {
              pageName: "エッジデバイス",
              onClick: () => navigate("/aimo_parking/edge_device"),
            },
            {
              pageName: edgeDevice?.serialNumber ?? "読み込み中",
              onClick: () => navigate(`/aimo_parking/edge_device/${edgeDeviceId}`),
            },
          ]}
        />
      </TitleArea>
      {edgeDevice && (
        <Wrap>
          <ContentArea>
            <FormRow>
              <Label>シリアルナンバー</Label>
              <Value>{edgeDevice.serialNumber}</Value>
            </FormRow>
            <FormRow>
              <Label>現在のステータス</Label>
              <Value>
                {generateAimoParkingEdgeDeviceStatusJa(
                  edgeDevice.status as AimoParkingEdgeDeviceStatus
                ) ?? "-"}
              </Value>
            </FormRow>
            <FormRow>
              <Label>変更後のステータス</Label>
              <AimoParkingSelect
                choices={choices}
                value={selectedChoice}
                onChange={setSelectedChoice}
                width="350px"
                placeholder="ステータスを選択"
              />
            </FormRow>
          </ContentArea>
          <FooterArea>
            <CancelWrap>
              <Button
                type="secondary"
                onClick={() => navigate(`/aimo_parking/edge_device/${edgeDeviceId}`)}
                label="キャンセル"
                width="160px"
              />
            </CancelWrap>
            <SaveWrap>
              <Annotation>更新するまで入力内容は反映されません</Annotation>
              <Button
                type="primary"
                onClick={handleOpenSubmitModal}
                label="ステータスを更新"
                disabled={!canSubmit}
                width="191px"
              />
            </SaveWrap>
          </FooterArea>
        </Wrap>
      )}
      <UserModal isVisible={isVisible} config={modalConfig} />
    </Board>
  );
};

export default EdgeDeviceUpdateStatus;
